/**
 * Offline outbox (docs/08 §7): Dexie table `outbox` + replay triggers + React hooks.
 * The logic (order, idempotency, error classification) is in `outbox-core.ts`.
 *
 * Offline-capable mutations (reports, traffic confirmations, movements, signatures, photos) go
 * through `enqueue()` instead of `api.post/put`:
 *
 *   const result = await enqueue<Report>({
 *     method: 'POST', url: '/reports', body, label: 'Report Santa Croce 10:30',
 *     invalidates: ['reports', 'dashboard'],
 *   });
 *   if (result.status === 'queued') toast({ title: 'Salvato sul dispositivo: verrà inviato appena torna la rete' });
 *
 * Rows created offline can show "In attesa di invio" with `useOutboxEntries('/reports')`.
 */
import Dexie from 'dexie';
import type { Table } from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { useSyncExternalStore } from 'react';
import { apiFetch } from './api';
import {
  createMemoryStore,
  createOutbox,
  formDataFromMultipart,
  multipartFromFormData,
} from './outbox-core';
import type {
  EnqueueInput,
  EnqueueResult,
  FlushSummary,
  OutboxEntry,
  OutboxMultipart,
  OutboxStore,
} from './outbox-core';
import { queryClient } from './query';
import { uuid } from './utils';

export type { EnqueueResult, FlushSummary, OutboxEntry } from './outbox-core';

/** Interval of the periodic replay. */
export const OUTBOX_FLUSH_INTERVAL_MS = 15_000;

class CarteDb extends Dexie {
  outbox!: Table<OutboxEntry, number>;

  constructor() {
    super('fmf-carte');
    this.version(1).stores({ outbox: '++seq, id, userId, status' });
  }
}

const db = typeof indexedDB === 'undefined' ? null : new CarteDb();

function dexieStore(table: Table<OutboxEntry, number>): OutboxStore {
  return {
    add: (entry) => table.add(entry),
    update: async (seq, changes) => {
      await table.update(seq, changes);
    },
    remove: (seq) => table.delete(seq),
    get: (seq) => table.get(seq),
    list: (userId) =>
      userId == null
        ? table.orderBy('seq').toArray()
        : table.where('userId').equals(userId).sortBy('seq'),
  };
}

/** Falls back to memory when IndexedDB fails (private browsing on old browsers). */
function resilientStore(): OutboxStore {
  const memory = createMemoryStore();
  if (!db) return memory;
  const primary = dexieStore(db.outbox);
  let broken = false;
  const pick = () => (broken ? memory : primary);
  return {
    add: async (entry) => {
      try {
        return await pick().add(entry);
      } catch {
        broken = true;
        return memory.add(entry);
      }
    },
    update: (seq, changes) => pick().update(seq, changes),
    remove: (seq) => pick().remove(seq),
    get: (seq) => pick().get(seq),
    list: (userId) => pick().list(userId),
  };
}

// ---------------------------------------------------------------- current user

let currentUserId: number | null = null;
const userListeners = new Set<() => void>();
/** End of the round cancelled by the last change of user. */
let userSwitch: Promise<void> = Promise.resolve();

/**
 * Set by the auth layer: only the entries of the logged-in user are sent. On a change of user the
 * round in progress is cancelled at once (no entry of the previous user leaves after this call);
 * the returned promise resolves when that round has ended, so the caller can wait for it before
 * the session cookie changes (logout, login). The new user's round starts after it.
 */
export function setOutboxUser(userId: number | null): Promise<void> {
  if (currentUserId === userId) return userSwitch;
  currentUserId = userId;
  for (const l of userListeners) l();
  const stopped = outbox.abort();
  userSwitch = stopped.then(() => {
    if (userId != null && currentUserId === userId) void outbox.flush();
  });
  return userSwitch;
}

function useOutboxUser(): number | null {
  return useSyncExternalStore(
    (cb) => {
      userListeners.add(cb);
      return () => userListeners.delete(cb);
    },
    () => currentUserId,
    () => null,
  );
}

// ---------------------------------------------------------------- outbox instance

function withWebLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator === 'undefined' || !navigator.locks) return fn();
  return navigator.locks.request('fmf-carte-outbox', fn) as Promise<T>;
}

const outbox = createOutbox({
  store: resilientStore(),
  send: (entry, signal) =>
    apiFetch(entry.url, {
      method: entry.method,
      body: entry.blobs ? undefined : entry.body,
      formData: entry.blobs ? formDataFromMultipart(entry.blobs) : undefined,
      idempotencyKey: entry.id,
      signal,
    }),
  isOnline: () => typeof navigator === 'undefined' || navigator.onLine,
  currentUserId: () => currentUserId,
  onSent: (entry) => {
    for (const entity of entry.invalidates) {
      void queryClient.invalidateQueries({ queryKey: [entity] });
    }
  },
  withLock: withWebLock,
  newId: uuid,
});

export type EnqueueRequest = Omit<EnqueueInput, 'blobs'> & {
  /** Multipart upload; stored as Blobs while waiting. */
  formData?: FormData;
};

/**
 * Sends a mutation now if possible, otherwise keeps it on the device until the network is back.
 * The files of a multipart body are read into memory here, before anything is stored
 * (`multipartFromFormData`): an unreadable or empty file is refused at once.
 */
export async function enqueue<T>(request: EnqueueRequest): Promise<EnqueueResult<T>> {
  const { formData, ...rest } = request;
  const blobs: OutboxMultipart | undefined = formData
    ? await multipartFromFormData(formData)
    : undefined;
  return outbox.enqueue<T>({ ...rest, blobs });
}

export const flushOutbox = (): Promise<FlushSummary> => outbox.flush();
export const retryOutboxEntry = (seq: number): Promise<FlushSummary> => outbox.retry(seq);
export const discardOutboxEntry = (seq: number): Promise<void> => outbox.discard(seq);

// Diagnostics (end-to-end tests, support): with `localStorage['carte:debug'] = '1'` the outbox is
// reachable from the console as `window.carteOutbox` (same rights as the page's own scripts).
try {
  if (typeof window !== 'undefined' && window.localStorage.getItem('carte:debug') === '1') {
    Object.assign(window, { carteOutbox: { enqueue, flush: flushOutbox } });
  }
} catch {
  // storage blocked: no diagnostics
}

/** Number of entries of a user still waiting (asked before logging out). */
export async function countUnsent(userId: number): Promise<number> {
  if (!db) return 0;
  try {
    return await db.outbox.where('userId').equals(userId).count();
  } catch {
    return 0;
  }
}

/**
 * Replay triggers while a user is logged in (mounted by AppShell): back online, tab visible again,
 * every 15 s, and once at start.
 */
export function startOutboxSync(): () => void {
  const flush = () => void outbox.flush();
  const onVisible = () => {
    if (document.visibilityState === 'visible') flush();
  };
  window.addEventListener('online', flush);
  document.addEventListener('visibilitychange', onVisible);
  const timer = window.setInterval(flush, OUTBOX_FLUSH_INTERVAL_MS);
  flush();
  return () => {
    window.removeEventListener('online', flush);
    document.removeEventListener('visibilitychange', onVisible);
    window.clearInterval(timer);
  };
}

// ---------------------------------------------------------------- hooks

function useFlushing(): boolean {
  return useSyncExternalStore(outbox.subscribe, outbox.isFlushing, () => false);
}

const NO_ENTRIES: OutboxEntry[] = [];

export interface OutboxState {
  /** Entries of the logged-in user, in replay order. */
  entries: OutboxEntry[];
  /** Entries of other users left on this device (sent at their next login). */
  otherUsers: number;
  pending: number;
  failed: number;
  flushing: boolean;
  /** False until IndexedDB has been read once (avoid showing an empty queue too early). */
  loaded: boolean;
}

export function useOutbox(): OutboxState {
  const userId = useOutboxUser();
  const result = useLiveQuery(async () =>
    db
      ? await db.outbox
          .orderBy('seq')
          .toArray()
          .catch(() => NO_ENTRIES)
      : NO_ENTRIES,
  );
  const flushing = useFlushing();
  const all = result ?? NO_ENTRIES;
  const entries = all.filter((e) => e.userId === userId);
  return {
    entries,
    otherUsers: all.length - entries.length,
    pending: entries.filter((e) => e.status !== 'error').length,
    failed: entries.filter((e) => e.status === 'error').length,
    flushing,
    loaded: result !== undefined,
  };
}

/** Counters for the sync indicator. */
export function useOutboxStatus(): { pending: number; failed: number; flushing: boolean } {
  const { pending, failed, flushing } = useOutbox();
  return { pending, failed, flushing };
}

/** Entries of the current user whose URL starts with `urlPrefix` (e.g. '/reports'). */
export function useOutboxEntries(urlPrefix: string): OutboxEntry[] {
  const { entries } = useOutbox();
  const prefix = urlPrefix.startsWith('/api') ? urlPrefix.slice(4) : urlPrefix;
  return entries.filter((e) => e.url.startsWith(prefix) || e.url.startsWith(`/api${prefix}`));
}
